import * as React from 'react';
import { useI18n } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import { CriteriaBaseForm } from '~/connect/components';
import { FormContainer, Icon, SoftBadge, useConfirmation } from '@wyz/ui';
import { useBoolean } from '@wyz/utils';
import { useForm, useFormContext, UseFormReturn } from 'react-hook-form';
import { ROLES } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { criteriaFormSchema } from '~/connect/schemas';

type EditableBadgeContextValue = {
  isEditing: boolean;
  open: () => void;
  close: () => void;
  methods: UseFormReturn<{ name: string }>;
  onSubmit: (value: string) => void;
};

const EditableBadgeContext =
  React.createContext<EditableBadgeContextValue | null>(null);

const useEditableBadge = () => {
  const context = React.useContext(EditableBadgeContext);
  if (!context) {
    throw new Error(
      'EditableBadge compound components must be used within EditableBadge.Root',
    );
  }
  return context;
};

type RootProps = {
  value: string;
  onSubmit: (value: string) => void;
  children: React.ReactNode;
};

function Root({ value, onSubmit, children }: RootProps) {
  const [isEditing, handlers] = useBoolean();
  const methods = useForm<{ name: string }>({
    resolver: zodResolver(criteriaFormSchema),
    defaultValues: {
      name: value,
    },
  });

  const handleSubmit = React.useCallback(
    (newValue: string) => {
      onSubmit(newValue);
      handlers.setFalse();
    },
    [onSubmit, handlers],
  );

  const contextValue = React.useMemo(
    () => ({
      isEditing,
      open: handlers.setTrue,
      close: handlers.setFalse,
      methods,
      onSubmit: handleSubmit,
    }),
    [isEditing, handlers, methods, handleSubmit],
  );

  return (
    <EditableBadgeContext.Provider value={contextValue}>
      {children}
    </EditableBadgeContext.Provider>
  );
}

type DisplayProps = {
  value: string;
  onDelete: () => void;
  reserved: boolean;
  isDefault?: boolean;
};

function Display({
  value,
  onDelete,
  reserved,
  isDefault = false,
}: DisplayProps) {
  const { t } = useI18n();
  const { isEditing, open } = useEditableBadge();
  const acl = useAccessControl();
  const ask = useConfirmation();

  if (isEditing) {
    return null;
  }

  return (
    <SoftBadge
      bg='classic'
      className='fs-xs position-relative'
      onDoubleClick={() => {
        if (!reserved && !isDefault) {
          open();
        }
      }}
      pill
    >
      <span className='align-middle'>{value}</span>
      {(!reserved || isDefault) &&
        acl.isGranted([ROLES.ROLE_CRITERIA_DELETE]) && (
          <Icon
            icon='close'
            role='button'
            className='cursor-pointer'
            id={`delete-criterion-value-${value}`}
            width={15}
            height={15}
            onClick={() => {
              ask({
                title: t('app.criteria.delete_value.confirmation.title', {
                  criterionValue: value,
                }),
                description: t(
                  'app.criteria.delete_value.confirmation.description',
                  { criterionValue: value },
                ),
                cancelBtnText: t('app.common.button.cancel'),
                confirmBtnText: t('app.common.buttons.delete'),
                confirmBtnVariant: 'danger',
              }).onConfirm(() => {
                onDelete();
              });
            }}
          />
        )}
    </SoftBadge>
  );
}

type FormProps = {
  children?: React.ReactNode;
};

function Form({ children }: FormProps) {
  const { isEditing, close, methods, onSubmit } = useEditableBadge();

  if (!isEditing) {
    return null;
  }

  return (
    <SoftBadge bg='classic' className='fs-xs position-relative' pill>
      <FormContainer
        methods={methods}
        submitHandler={(data) => {
          onSubmit(data.name);
        }}
      >
        {children || <CriteriaBaseForm name='name' onCancel={close} />}
      </FormContainer>
    </SoftBadge>
  );
}

type InputProps = {
  name: string;
  children?: React.ReactNode;
};

function Input({ name, children }: InputProps) {
  const { isEditing, close, onSubmit } = useEditableBadge();
  const methods = useFormContext<Record<string, string>>();

  if (!isEditing) {
    return null;
  }

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const value = methods.getValues(name);
    if (value) {
      onSubmit(value);
    }
  };

  return (
    <SoftBadge bg='classic' className='fs-xs position-relative' pill>
      {children || (
        <CriteriaBaseForm
          name={name}
          onCancel={close}
          onSubmit={handleSubmit}
        />
      )}
    </SoftBadge>
  );
}

const EditableBadge = {
  Root,
  Display,
  Form,
  Input,
};

export default EditableBadge;
